import { handleSupabaseList, handleSupabaseSingle, supabase } from "@/lib/supabase-unified";
import {
  CategoryType,
  CourseStatus,
  type CourseSkillLevelLabel,
} from "@/lib/constants";
import { effectiveMinStatusForCourse } from "@/lib/draftVisibility";
import type { Course } from "@/types";

export type SkillLevel = CourseSkillLevelLabel;

export type SkillPathwayCourse = Course & {
  level: SkillLevel;
  teacher_name?: string | null;
};

export interface SkillPathwayPageData {
  id: number;
  title: string;
  slug: string;
  courses: SkillPathwayCourse[];
}

function mapPositionToLevel(position: number | null): CourseSkillLevelLabel {
  const p = position ?? 0;
  if (p < 200) {
    return p < 100 ? "beginner" : "intermediate";
  }
  return "advanced";
}

export async function getSkillPathwayBySlug(
  slug: string,
  minStatus: number = CourseStatus.Published,
): Promise<SkillPathwayPageData> {
  const category = await handleSupabaseSingle<{
    id: number;
    title: string;
    slug: string;
  }>(
    supabase
      .from("category")
      .select("id, title, slug")
      .eq("slug", slug)
      .eq("type", 6)
      .single() as any
  );

  const rows = await handleSupabaseList<{
    position: number | null;
    course: Course | null;
  }>(
    supabase
      .from("course_categories")
      .select(
        `
        position,
        course:courses (
          id,
          title,
          slug,
          status,
          thumbnail,
          summary,
          created_at,
          no_of_lessons,
          no_of_gps,
          metadata
        )
      `
      )
      .eq("category", category.id)
      .gte("course.status", minStatus)
      .order("position", { ascending: true }) as any
  );

  const courseList = rows
    .filter(
      (row): row is typeof row & { course: NonNullable<typeof row.course> } =>
        !!row.course &&
        (row.course.status ?? 0) >=
          effectiveMinStatusForCourse(minStatus, row.course.metadata),
    )
    .map((row) => {
      const level = mapPositionToLevel(row.position);
      return {
        ...(row.course as Course),
        level,
      };
    });

  const courseIds = courseList.map((c) => c.id);
  const instructorsByCourse: Record<number, string[]> = {};

  if (courseIds.length > 0) {
    const instructorRows = await handleSupabaseList<{
      course_id: number;
      instructor: { name: string } | null;
    }>(
      supabase
        .from("instructor_courses")
        .select("course_id, instructor:instructors(name)")
        .in("course_id", courseIds)
        .order("course_id", { ascending: true })
        .order("position", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true }) as any
    );

    for (const row of instructorRows) {
      if (row.instructor?.name) {
        (instructorsByCourse[row.course_id] ??= []).push(row.instructor.name);
      }
    }
  }

  const courses: SkillPathwayCourse[] = courseList.map((c) => ({
    ...c,
    teacher_name: instructorsByCourse[c.id]?.join(" & ") ?? null,
  }));

  return {
    id: category.id,
    title: category.title,
    slug: category.slug,
    courses,
  };
}

export type SkillPathwaySibling = {
  id: number;
  title: string;
  slug: string;
  section_id: number;
  section_title: string;
  section_slug: string;
  position: number;
  thumbnail: string | null;
  color: string | null;
};

export async function fetchSiblingSkillPathwaysByPathwaySlug(
  pathwaySlug: string,
): Promise<SkillPathwaySibling[]> {
  try {
    const pathwayCat = await handleSupabaseSingle<{ id: number }>(
      supabase
        .from("category")
        .select("id")
        .eq("slug", pathwaySlug)
        .eq("type", CategoryType.SkillPathway)
        .single() as any,
    );

    const linkRows = await handleSupabaseList<{
      skill_pathway_section_id: number;
    }>(
      supabase
        .from("category_categories")
        .select("skill_pathway_section_id")
        .eq("skill_pathway_id", pathwayCat.id)
        .limit(1) as any,
    );

    const sectionId = linkRows[0]?.skill_pathway_section_id;
    if (sectionId == null) return [];

    const section = await handleSupabaseSingle<{
      id: number;
      title: string;
      slug: string;
    }>(
      supabase
        .from("category")
        .select("id, title, slug")
        .eq("id", sectionId)
        .single() as any,
    );

    const rows = await handleSupabaseList<{
      position: number | null;
      pathway: {
        id: number;
        title: string;
        slug: string;
        thumbnail: string | null;
        color: string | null;
      } | null;
    }>(
      supabase
        .from("category_categories")
        .select(
          "position, pathway:skill_pathway_id ( id, title, slug, thumbnail, color )",
        )
        .eq("skill_pathway_section_id", sectionId)
        .order("position", { ascending: true }) as any,
    );

    return rows
      .filter((r): r is typeof r & { pathway: NonNullable<typeof r.pathway> } => !!r.pathway)
      .map((r) => ({
        id: r.pathway.id,
        title: r.pathway.title,
        slug: r.pathway.slug,
        section_id: section.id,
        section_title: section.title,
        section_slug: section.slug,
        position: r.position ?? 0,
        thumbnail: r.pathway.thumbnail ?? null,
        color: r.pathway.color ?? null,
      }));
  } catch {
    return [];
  }
}
